import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Department } from "models/Department";
import { CreateDepartmentRequest } from "api/departments/createDepartment";
import { toast } from "react-toastify";
import { useDeleteDepartmentMutation, useUpdateDepartmentMutation } from "app/api/departments";
import { useCreateDepartmentMutation } from "app/api/departments";
import { getErrorMessage } from "utilities/getErrorMessage";
import LargeDialog from "components/LargeDialog";
import { Button, TextField } from "@mui/material";
import Clear from "@mui/icons-material/Clear";
import Check from "@mui/icons-material/Check";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { useState } from "react";
import SimpleDialog from "components/SimpleDialog";

type UpsertDepartmentDialogProps = {
  department?: Department | null;
  onClose: () => void;
};

const UpsertDepartmentDialog: React.FC<UpsertDepartmentDialogProps> = (props) => {
  const { department: departmentToEdit, onClose } = props;

  const [createDepartment, { isLoading: isCreating }] = useCreateDepartmentMutation();
  const [updateDepartment, { isLoading: isUpdating }] = useUpdateDepartmentMutation();
  const [deleteDepartment, { isLoading: isDeleting }] = useDeleteDepartmentMutation();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const create = !departmentToEdit;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateDepartmentRequest>();

  const onSubmit: SubmitHandler<CreateDepartmentRequest> = (data) => {
    const requestData = {
      name: data.name,
    } as CreateDepartmentRequest;

    (create
      ? createDepartment(requestData)
      : updateDepartment({ id: departmentToEdit!.id, ...requestData })
    )
      .unwrap()
      .then(() => {
        toast.success(`Avdelningen ${create ? "skapades" : "uppdaterades"}`);
        onClose();
      })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  const onDelete = () => {
    if (!departmentToEdit) return;
    deleteDepartment({ id: departmentToEdit.id })
      .unwrap()
      .then(() => {
        toast.success("Avdelningen raderades");
        onClose();
      })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  return (
    <LargeDialog
      title={create ? "Skapa avdelning" : "Redigera avdelning"}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      actions={
        <>
          <Button
            variant="outlined"
            color="error"
            hidden={create}
            disabled={isCreating || isUpdating}
            loading={isDeleting}
            startIcon={<DeleteOutlined />}
            sx={{ mr: "auto" }}
            onClick={() => setShowDeleteConfirmation(true)}
          >
            Radera
          </Button>

          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isCreating || isUpdating || isDeleting}
            startIcon={<Clear />}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={isDeleting}
            loading={isCreating || isUpdating}
            color="success"
            startIcon={<Check />}
          >
            {create ? "Skapa" : "Spara"}
          </Button>
        </>
      }
    >
      <Controller
        name="name"
        control={control}
        defaultValue={departmentToEdit?.name}
        rules={{ required: "Namn" }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Namn"
            type="text"
            autoComplete="new-password"
            variant="filled"
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        )}
      />

      {showDeleteConfirmation && (
        <SimpleDialog
          title="Radera avdelning?"
          onClose={() => setShowDeleteConfirmation(false)}
          confirmProps={{ color: "error", loading: isDeleting }}
          dismissProps={{ disabled: isDeleting }}
          onConfirm={onDelete}
        />
      )}
    </LargeDialog>
  );
};

export default UpsertDepartmentDialog;
